import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import api from '../api/client.js';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtMoney(n, { blankZero = true } = {}) {
  const v = Number(n || 0);
  if (v === 0 && blankZero) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
function fmtMoney2(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function gpPct(gp, sales) {
  const s = Number(sales || 0);
  if (s <= 0) return '—';
  return `${((Number(gp || 0) / s) * 100).toFixed(1)}%`;
}
function fmtCoverage(cov) {
  if (!cov) return '—';
  const a = `${MONTH_NAMES[cov.first.month - 1]} ${cov.first.year}`;
  const b = `${MONTH_NAMES[cov.last.month - 1]} ${cov.last.year}`;
  return `${a} to ${b} (${cov.months} mo)`;
}

/**
 * Sales tab: monthly sales and GP from ASE's D3 invoices, with an invoice
 * drill-down per month (and an "all recent invoices" view).
 */
export default function SalesHistory({ customerId }) {
  const [expanded, setExpanded] = useState(false);
  const [drill, setDrill] = useState(null); // { year, month } | { all: true } | null
  const q = useQuery({
    queryKey: ['sales-history', customerId],
    queryFn: () => api.get(`/sales/customers/${customerId}/history`).then(r => r.data.data),
  });

  if (q.isLoading) {
    return (
      <section className="card p-4">
        <div className="section-eyebrow">Sales history</div>
        <div className="rule-crimson" />
        <div className="text-muted text-sm mt-2">Loading…</div>
      </section>
    );
  }

  if (q.isError) {
    return (
      <section className="card p-4">
        <div className="section-eyebrow">Sales history</div>
        <div className="rule-crimson" />
        <div className="text-muted text-sm mt-2">
          {q.error?.response?.data?.error || 'Failed to load sales history.'}
        </div>
      </section>
    );
  }

  const { summary, monthly } = q.data;
  const hasAnyHistory = monthly.length > 0;
  const t12 = summary.trailing_12mo;

  const ordered = [...monthly].sort((a, b) => (b.year - a.year) || (b.month - a.month));
  const visible = expanded ? ordered : ordered.slice(0, 12);

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="section-eyebrow">Sales history</div>
        <button className="text-xs text-crimson hover:underline"
                onClick={() => setDrill(drill?.all ? null : { all: true })}>
          {drill?.all ? 'hide recent invoices' : 'View recent invoices'}
        </button>
      </div>
      <div className="rule-crimson" />

      {!hasAnyHistory && (
        <div className="text-muted text-sm">No posted invoices on file for this customer.</div>
      )}

      {hasAnyHistory && (
        <>
          <div>
            <div className="text-xs text-muted mb-1">Last 12 complete months</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-navy text-white rounded p-2">
                <div className="text-[11px] uppercase tracking-wide opacity-80">Sales</div>
                <div className="font-semibold text-sm">{fmtMoney(t12.sales, { blankZero: false })}</div>
              </div>
              <div className="bg-panel rounded p-2">
                <div className="text-[11px] text-muted uppercase tracking-wide">GP</div>
                <div className="font-semibold text-sm">{fmtMoney(t12.gp, { blankZero: false })}</div>
              </div>
              <div className="bg-panel rounded p-2">
                <div className="text-[11px] text-muted uppercase tracking-wide">GP %</div>
                <div className="font-semibold text-sm">{gpPct(t12.gp, t12.sales)}</div>
              </div>
              <div className="bg-panel rounded p-2">
                <div className="text-[11px] text-muted uppercase tracking-wide">Invoices</div>
                <div className="font-semibold text-sm">{t12.invoices}</div>
              </div>
            </div>
          </div>

          <div className="text-xs text-muted">
            On file: {fmtCoverage(summary.coverage)} · lifetime sales {fmtMoney(summary.lifetime.sales, { blankZero: false })},
            GP {fmtMoney(summary.lifetime.gp, { blankZero: false })}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b">
                  <th className="py-1 pr-2">Month</th>
                  <th className="py-1 pr-2 text-right">Sales</th>
                  <th className="py-1 pr-2 text-right">GP</th>
                  <th className="py-1 pr-2 text-right">GP %</th>
                  <th className="py-1 pl-2 text-right">Invoices</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(r => {
                  const active = drill && !drill.all && drill.year === r.year && drill.month === r.month;
                  return (
                    <tr key={`${r.year}-${r.month}`} className="border-b last:border-0">
                      <td className="py-1 pr-2 whitespace-nowrap">{MONTH_NAMES[r.month - 1]} {r.year}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">
                        <button
                          className={clsx('hover:underline', active ? 'text-crimson font-medium' : 'text-navy')}
                          onClick={() => setDrill(active ? null : { year: r.year, month: r.month })}
                          title="View invoices">
                          {fmtMoney(r.sales, { blankZero: false })}
                        </button>
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums">{fmtMoney(r.gp, { blankZero: false })}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{gpPct(r.gp, r.sales)}</td>
                      <td className="py-1 pl-2 text-right tabular-nums">{r.invoices}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {ordered.length > 12 && (
            <button type="button" onClick={() => setExpanded(v => !v)}
                    className="text-xs text-crimson hover:underline">
              {expanded ? 'Show last 12 months only' : `Show all ${ordered.length} months`}
            </button>
          )}

          {!drill && (
            <div className="text-[11px] text-muted">Tip: click a month&apos;s sales to see its invoices.</div>
          )}
        </>
      )}

      {drill && (
        <InvoiceDrill customerId={customerId} {...drill} onClose={() => setDrill(null)} />
      )}
    </section>
  );
}

function InvoiceDrill({ customerId, year, month, all, onClose }) {
  const qs = all ? 'limit=100' : `year=${year}&month=${month}`;
  const q = useQuery({
    queryKey: ['cust-invoices', customerId, all ? 'all' : `${year}-${month}`],
    queryFn: () => api.get(`/customers/${customerId}/invoices?${qs}`).then(r => r.data.data),
  });
  const rows = q.data || [];
  const posted = rows.filter(r => r.status === 'posted');
  const total = posted.reduce((s, r) => s + Number(r.subtotal || 0), 0);
  const totalGp = posted.reduce((s, r) => s + Number(r.gp || 0), 0);

  return (
    <div className="border border-navy-200 rounded p-3 bg-panel/40">
      <div className="flex items-center justify-between gap-2">
        <div className="section-eyebrow">
          {all ? 'Recent invoices' : `Invoices: ${MONTH_NAMES[month - 1]} ${year}`}
          {rows.length > 0 && (
            <span className="text-muted normal-case"> · {rows.length} · posted {fmtMoney(total, { blankZero: false })}, GP {fmtMoney(totalGp, { blankZero: false })}</span>
          )}
        </div>
        <button className="text-xs text-crimson hover:underline" onClick={onClose}>close ✕</button>
      </div>
      {q.isLoading && <div className="text-muted text-sm mt-2">Loading…</div>}
      {q.isError && <div className="text-crimson text-sm mt-2">{q.error?.response?.data?.error || 'Failed to load invoices.'}</div>}
      {!q.isLoading && !q.isError && rows.length === 0 && (
        <div className="text-muted text-sm mt-2">No invoices on file for this period.</div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs">
            <thead className="text-left text-muted uppercase tracking-wider">
              <tr>
                <th className="py-1 pr-3">Date</th>
                <th className="py-1 pr-3">Invoice</th>
                <th className="py-1 pr-3">Type</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1 pr-3">Order</th>
                <th className="py-1 pr-3">Salesperson</th>
                <th className="py-1 pr-3 text-right">Subtotal</th>
                <th className="py-1 pr-3 text-right">GP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(inv => {
                const isVoid = inv.status === 'void';
                return (
                  <tr key={inv.id} className={clsx('border-t border-panel', isVoid && 'text-muted line-through')}>
                    <td className="py-1 pr-3 whitespace-nowrap">
                      {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-1 pr-3 font-mono text-[11px]">{inv.invoice_number}</td>
                    <td className="py-1 pr-3">
                      {inv.invoice_type === 'credit'
                        ? <span className="text-crimson font-medium">Credit</span>
                        : 'Invoice'}
                    </td>
                    <td className="py-1 pr-3">
                      {isVoid
                        ? <span className="no-underline px-1 py-0.5 rounded bg-panel text-muted uppercase text-[9px] tracking-wider">Void</span>
                        : 'Posted'}
                    </td>
                    <td className="py-1 pr-3 font-mono text-[11px]">{inv.order_number || '—'}</td>
                    <td className="py-1 pr-3">{inv.salesperson_name || inv.salesperson_code || '—'}</td>
                    <td className={clsx('py-1 pr-3 text-right tabular-nums', Number(inv.subtotal) < 0 && !isVoid && 'text-crimson')}>
                      {fmtMoney2(inv.subtotal)}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">{fmtMoney2(inv.gp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-[10px] text-muted mt-1">Voided invoices are shown struck through and are not counted in sales.</div>
        </div>
      )}
    </div>
  );
}
